import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ArrowUpRight } from 'lucide-react';

const demoCases = [
  {
    id: '1',
    title: 'Lagerhal i Køge',
    building_type: 'lager',
    building_type_label: 'Lager/logistik',
    solution_type: 'energioptimering',
    solution_type_label: 'Energioptimering',
    year: 2024,
    challenge: 'Høje elomkostninger og dårlige lysforhold i en 5.000 m² lagerhal med ældre HQL-armaturer.',
    solution: 'Udskiftning af 120 HQL-armaturer til LED med bevægelsessensorer og zoneopdeling.',
    effect: '65% reduktion i elforbrug svarende til 180.000 kr. årligt.',
    energy_savings_percent: 65,
    image: '/images/film-grabs/still-9.jpg',
    beforeImage: '/images/film-grabs/still-12.jpg',
    afterImage: '/images/film-grabs/still-13.jpg',
  },
  {
    id: '2',
    title: 'Kontorbygning i København',
    building_type: 'kontor',
    building_type_label: 'Kontor',
    solution_type: 'lysstyring',
    solution_type_label: 'Intelligent lysstyring',
    year: 2024,
    challenge: 'Behov for fleksibel belysning i storrumskontor med skiftende arbejdspladser.',
    solution: 'Implementation af DALI-styring med dagslyssensorer og tilstedeværelsessensorer.',
    effect: '55% reduktion i elforbrug til belysning.',
    energy_savings_percent: 55,
    image: '/images/film-grabs/still-10.jpg',
    beforeImage: '/images/film-grabs/still-14.jpg',
    afterImage: '/images/film-grabs/still-15.jpg',
  },
  {
    id: '3',
    title: 'Produktionshal i Roskilde',
    building_type: 'produktion',
    building_type_label: 'Produktion',
    solution_type: 'energioptimering',
    solution_type_label: 'LED-udskiftning',
    year: 2023,
    challenge: 'Forældet belysning med høje vedligeholdelsesomkostninger.',
    solution: 'Komplet LED-renovering med nødstyring og arbejdsmiljøvenlig belysning.',
    effect: '70% energibesparelse samt bedre arbejdsforhold.',
    energy_savings_percent: 70,
    image: '/images/film-grabs/still-11.jpg',
    beforeImage: '/images/film-grabs/still-16.jpg',
    afterImage: '/images/film-grabs/still-17.jpg',
  },
  {
    id: '4',
    title: 'Idrætshal i Greve',
    building_type: 'idraetshal',
    building_type_label: 'Idrætshal',
    solution_type: 'lysstyring',
    solution_type_label: 'Intelligent lysstyring',
    year: 2023,
    challenge: 'Behov for forskellige lysscenarier til forskellige aktiviteter.',
    solution: 'LED med scenariostyring for boldspil, gymnastik og arrangementer.',
    effect: '60% reduktion i elforbrug.',
    energy_savings_percent: 60,
    image: '/images/film-grabs/still-18.jpg',
    beforeImage: '/images/film-grabs/still-19.jpg',
    afterImage: '/images/film-grabs/still-20.jpg',
  },
];

export const CasesListPage = () => {
  const [filter, setFilter] = useState('all');
  const [filteredCases, setFilteredCases] = useState(demoCases);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredCases(demoCases);
    } else {
      setFilteredCases(demoCases.filter(c => c.solution_type === filter));
    }
  }, [filter]);

  return (
    <main data-testid="cases-page" className="pt-24">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Cases
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-medium text-[#0E3D24] mt-6 leading-[1.1]">
              Dokumenteret effekt
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-[#A6B8A2]/30">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="flex gap-8">
            {[
              { value: 'all', label: 'Alle' },
              { value: 'energioptimering', label: 'Energioptimering' },
              { value: 'lysstyring', label: 'Lysstyring' },
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => setFilter(item.value)}
                className={`text-[11px] tracking-[0.15em] uppercase transition-colors ${
                  filter === item.value ? 'text-[#0E3D24]' : 'text-[#A6B8A2] hover:text-[#1F5A3A]'
                }`}
                data-testid={`filter-${item.value}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-12">
            {filteredCases.map((caseItem, index) => (
              <motion.div
                key={caseItem.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`case-card-${caseItem.id}`}
              >
                <Link to={`/cases/${caseItem.id}`} className="group block">
                  <div className="aspect-[3/2] bg-[#A6B8A2]/20 overflow-hidden mb-8">
                    {caseItem.image ? (
                      <img loading="lazy" 
                        src={caseItem.image} 
                        alt={caseItem.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#A6B8A2] group-hover:scale-105 transition-transform duration-700">
                        <span className="text-sm">Billede</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[11px] tracking-[0.1em] uppercase text-[#A6B8A2] mb-3">
                        {caseItem.solution_type_label} — {caseItem.year}
                      </p>
                      <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-[#1E2A24]">
                        {caseItem.title}
                      </h2>
                      <p className="text-[#1F5A3A] mt-3">
                        {caseItem.energy_savings_percent}% reduktion
                      </p>
                    </div>
                    <ArrowUpRight 
                      size={20} 
                      className="text-[#A6B8A2] group-hover:text-[#0E3D24] transition-colors mt-1" 
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white mb-8">
              Vil I være næste succeshistorie?
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-white text-[#0E3D24] px-8 py-4 hover:bg-[#F4F2EE] transition-colors group"
            >
              <span>Book et møde</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export const CaseDetailPage = () => {
  const { id } = useParams();
  const caseItem = demoCases.find(c => c.id === id);

  if (!caseItem) {
    return (
      <main className="pt-32 pb-24">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <p className="text-[#A6B8A2]">Case ikke fundet</p>
          <Link to="/cases" className="inline-flex items-center gap-2 text-[#1E2A24] mt-4">
            <ArrowLeft size={14} />
            <span>Tilbage</span>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="case-detail-page" className="pt-24">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <Link 
            to="/cases" 
            className="inline-flex items-center gap-2 text-[#A6B8A2] hover:text-[#0E3D24] transition-colors mb-12"
          >
            <ArrowLeft size={14} />
            <span className="text-[11px] tracking-[0.15em] uppercase">Tilbage til cases</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2] mb-4">
              {caseItem.solution_type_label} — {caseItem.year}
            </p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] leading-[1.1]">
              {caseItem.title}
            </h1>
            <p className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#1F5A3A] mt-4">
              {caseItem.energy_savings_percent}% reduktion i elforbrug
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content - Text left, Image right */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Text content - Left */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <div>
                <h3 className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-4">Udfordring</h3>
                <p className="text-[#1E2A24] text-lg leading-[1.8]">{caseItem.challenge}</p>
              </div>
              <div>
                <h3 className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-4">Løsning</h3>
                <p className="text-[#1E2A24] text-lg leading-[1.8]">{caseItem.solution}</p>
              </div>
              <div>
                <h3 className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-4">Effekt</h3>
                <p className="text-[#1E2A24] text-lg leading-[1.8]">{caseItem.effect}</p>
              </div>
            </motion.div>

            {/* Images - Right */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div className="aspect-[4/3] bg-[#A6B8A2]/20 overflow-hidden">
                {caseItem.image ? (
                  <img loading="lazy" 
                    src={caseItem.image} 
                    alt={caseItem.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#A6B8A2]">
                    <span className="text-sm">Hovedbillede</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="aspect-square bg-[#A6B8A2]/20 overflow-hidden">
                  {caseItem.beforeImage ? (
                    <img loading="lazy" 
                      src={caseItem.beforeImage} 
                      alt="Før"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A6B8A2]">
                      <span className="text-xs">Før</span>
                    </div>
                  )}
                </div>
                <div className="aspect-square bg-[#A6B8A2]/20 overflow-hidden">
                  {caseItem.afterImage ? (
                    <img loading="lazy" 
                      src={caseItem.afterImage} 
                      alt="Efter"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A6B8A2]">
                      <span className="text-xs">Efter</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white mb-8">
              Vil I opnå samme resultat?
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-white text-[#0E3D24] px-8 py-4 hover:bg-[#F4F2EE] transition-colors group"
            >
              <span>Book et møde</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default function CasesPage() {
  return <CasesListPage />;
}
